import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FeatureFlag } from '@app/enums';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { Office } from '@app/models/company/office.model';
import { NotifyService } from '@app/services';
import { environment } from '@env/environment';
import { CompanyLogoService } from '@app/modules/settings/services';
import { Company } from '@app/models/company/company.model';
import { Industry } from '@app/models/company/industry.model';
import { isSameModel } from '@app/functions';

declare let google: any;

@Component({
    selector: 'app-form-setup-company',
    templateUrl: './setup-company.form.html',
    styles: [
        `
            .industry-option {
                display: flex;
                justify-content: space-between;
                width: 100%;
                align-items: center;
                color: inherit;
            }
        `,
    ],
})
export class SetupCompanyForm implements OnInit, OnDestroy {
    @ViewChild('form', { static: true }) form: NgForm;
    @Input() formData: any = {};
    @Input() company: Company;
    @Output() valid: EventEmitter<any> = new EventEmitter<any>();

    logo: File | null = null;
    industries: Industry[] = [];
    filteredIndustries: Industry[] = [];
    industrySearchValue = '';
    showCompanyOverviewAdditionalFields = false;

    private autocomplete;
    protected readonly isSameModel = isSameModel;

    constructor(
        public auth: AuthService,
        private changeDetectionRef: ChangeDetectorRef,
        private companyLogoService: CompanyLogoService,
        private featureService: FeatureService,
        private notify: NotifyService
    ) {}

    async ngOnInit(): Promise<void> {
        this.showCompanyOverviewAdditionalFields = await this.featureService.has(FeatureFlag.companyOverview);

        this.initAutocomplete();

        Industry.orderBy('groupName', 'ASC')
            .all()
            .then(([industries]) => {
                this.industries = industries;
                this.filteredIndustries = industries;
                this.setInitialIndustrySearchValue();
            });

        this.form.statusChanges.subscribe(() => {
            this.valid.emit({ valid: this.form.valid, data: this.formData });
        });
    }

    ngOnDestroy(): void {
        this.valid.emit({ valid: this.form.valid, data: this.formData });
    }

    filterIndustries(searchValue: string | number): void {
        if (typeof searchValue === 'number') {
            this.filteredIndustries = this.industries.filter((industry) => industry.id === searchValue);
            return;
        }

        if (!searchValue || searchValue.trim() === '') {
            this.filteredIndustries = this.industries;
            // Clear the selected industry if the search is empty
            this.formData.naicsIndustryId = null;
            return;
        }

        this.filteredIndustries = this.industries.filter((industry) => {
            const searchTerm = searchValue.toLowerCase();
            return (
                industry.groupName.toLowerCase().includes(searchTerm) ||
                industry.groupCode.toLowerCase().includes(searchTerm) ||
                `[${industry.groupCode}] ${industry.groupName}`.toLowerCase().includes(searchTerm)
            );
        });
    }

    onIndustrySelected(event: MatAutocompleteSelectedEvent): void {
        const selectedIndustry = this.industries.find((industry) => industry.id === event.option.value);
        if (selectedIndustry) {
            this.industrySearchValue = `[${selectedIndustry.groupCode}] ${selectedIndustry.groupName}`;
            this.formData.naicsIndustryId = event.option.value;
        }
    }

    private setInitialIndustrySearchValue(): void {
        if (this.formData.naicsIndustryId) {
            const selectedIndustry = this.industries.find((industry) => industry.id === this.formData.naicsIndustryId);
            if (selectedIndustry) {
                this.industrySearchValue = `[${selectedIndustry.groupCode}] ${selectedIndustry.groupName}`;
            }
        }
    }

    async saveLogo(file: File | null): Promise<void> {
        this.logo = file;
        if (!file) {
            this.formData.logoId = null;
            return;
        }

        try {
            this.companyLogoService.store(file);
        } catch (error) {
            this.notify.error(error);
        }
    }

    private initAutocomplete(): void {
        setTimeout(() => {
            $.getScript(
                `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places`,
                () => {
                    this.autocomplete = new google.maps.places.Autocomplete($('[name="address"]').get(0), {
                        types: ['address'],
                    });
                    this.autocomplete.addListener('place_changed', () => {
                        this.fillInAddress();
                    });
                }
            );
        });
    }

    private fillInAddress(): void {
        // Get the place details from the autocomplete object.
        const place = this.autocomplete.getPlace();
        if (!place) {
            return;
        }
        const address = Office.createFromGooglePlace(place);

        this.formData['address'] = address.addressLine1;
        this.formData['city'] = address.city;
        this.formData['province'] = address.province;
        this.formData['country'] = address.country;
        this.formData['postalCode'] = address.postalCode;
        this.changeDetectionRef.detectChanges();
    }
}
